/**
 * `/$tenantSlug/dashboard` — owner cockpit home.
 *
 * Per KEN_LAYER_7 §7.6 + ADR-021. The dashboard is a grid of widgets
 * the owner composes. Each widget comes from a module's manifest;
 * the cockpit reads the layout from /api/shell/dashboard/main and
 * writes back when the owner adds or removes a widget.
 *
 * v1 scope: render each widget as a placeholder card showing its
 * declared title + description (real per-widget data rendering wires
 * once each widget's component is implemented). Add and remove are
 * fully functional; drag-drop reorder + resize is a follow-up.
 *
 * Visibility filters (per ADR-021):
 *   - A widget is only addable if every module in `requires_modules`
 *     is enabled for the active tenant.
 *   - `requires_permissions` filtering against the user's permission
 *     set lands when the dashboard is permission-aware (today every
 *     authenticated owner sees every widget — fine for v1).
 */

import { useAuthStore } from "@ken/auth";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Chip,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Skeleton,
} from "@ken/ui";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactElement } from "react";

import { MODULES } from "../generated/modules";

export const Route = createFileRoute("/$tenantSlug/dashboard")({
  component: DashboardRoute,
});

interface WidgetDef {
  id: string;
  module_id: string;
  title: string;
  description: string;
  default_size: string; // e.g. "2x1"
  category: string;
  requires_modules: string[];
  requires_permissions: string[];
}

interface WidgetPlacement {
  widget_id: string;
  module_id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  config: Record<string, unknown>;
}

interface DashboardLayout {
  dashboard_id: string;
  widgets: WidgetPlacement[];
  source: "default" | "user" | "ai_suggested" | "ai_applied";
  updated_at: string;
}

function widgetCatalogue(): WidgetDef[] {
  const out: WidgetDef[] = [];
  for (const m of MODULES) {
    const manifest = m.manifest as unknown as {
      widgets?: Array<{
        id?: string;
        title?: string;
        description?: string;
        default_size?: string;
        category?: string;
        requires_modules?: string[];
        requires_permissions?: string[];
      }>;
    };
    for (const w of manifest.widgets ?? []) {
      if (!w.id) continue;
      out.push({
        id: w.id,
        module_id: m.name,
        title: w.title ?? w.id,
        description: w.description ?? "",
        default_size: w.default_size ?? "1x1",
        category: w.category ?? "other",
        requires_modules: w.requires_modules ?? [m.name],
        requires_permissions: w.requires_permissions ?? [],
      });
    }
  }
  return out;
}

function parseSize(size: string): { w: number; h: number } {
  const m = /^(\d+)x(\d+)$/.exec(size);
  if (!m) return { w: 1, h: 1 };
  return { w: Number(m[1]) || 1, h: Number(m[2]) || 1 };
}

function DashboardRoute(): ReactElement {
  const session = useAuthStore((s) => s.session);
  const enabledModules = useMemo(
    () => new Set(session?.enabledModules ?? []),
    [session?.enabledModules],
  );

  const [layout, setLayout] = useState<DashboardLayout | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const catalogue = useMemo(() => widgetCatalogue(), []);
  const placedIds = useMemo(
    () => new Set(layout?.widgets.map((w) => w.widget_id) ?? []),
    [layout],
  );

  const visibleCatalogue = useMemo(
    () =>
      catalogue.filter((w) => w.requires_modules.every((m) => enabledModules.has(m))),
    [catalogue, enabledModules],
  );

  const placedWithDef = useMemo(() => {
    if (!layout) return [];
    return layout.widgets.map((p) => ({
      placement: p,
      def: catalogue.find((c) => c.id === p.widget_id) ?? null,
    }));
  }, [layout, catalogue]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/shell/dashboard/main", { credentials: "include" });
        if (cancelled) return;
        if (res.status === 401 || res.status === 412) {
          setError("Your session expired.");
          return;
        }
        if (!res.ok) {
          setError(`Couldn't load dashboard (status ${res.status}).`);
          return;
        }
        setLayout((await res.json()) as DashboardLayout);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const save = async (widgets: WidgetPlacement[]): Promise<void> => {
    setError(null);
    try {
      const res = await fetch("/api/shell/dashboard/main", {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ widgets }),
      });
      if (!res.ok) {
        setError(`Couldn't save dashboard (status ${res.status}).`);
        return;
      }
      const fresh = (await res.json()) as DashboardLayout;
      setLayout(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Network error.");
    }
  };

  const addWidget = (def: WidgetDef): void => {
    if (!layout) return;
    const { w, h } = parseSize(def.default_size);
    const next: WidgetPlacement = {
      widget_id: def.id,
      module_id: def.module_id,
      x: 0,
      y: layout.widgets.length, // append at the bottom
      w,
      h,
      config: {},
    };
    void save([...layout.widgets, next]);
    setAddOpen(false);
  };

  const removeWidget = (widgetId: string): void => {
    if (!layout) return;
    void save(layout.widgets.filter((w) => w.widget_id !== widgetId));
  };

  const greetingName =
    session?.user.display_name?.split(/\s+/)[0] ??
    session?.user.email?.split("@")[0] ??
    "there";

  return (
    <div className="flex flex-col gap-6" data-testid="dashboard-root">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold mb-1">
            Owner
          </div>
          <h1 className="text-[32px] font-bold tracking-tight">Hi, {greetingName}</h1>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="primary"
              data-testid="dashboard-add-widget-button"
            >
              <Plus size={16} className="mr-1" />
              Add widget
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add a widget</DialogTitle>
              <DialogDescription>
                Pick a widget to add to your dashboard. Widgets needing a
                disabled module are hidden.
              </DialogDescription>
            </DialogHeader>
            <div
              className="flex flex-col gap-2 max-h-[480px] overflow-y-auto"
              data-testid="dashboard-widget-catalogue"
            >
              {visibleCatalogue.length === 0 ? (
                <div
                  data-testid="dashboard-widget-catalogue-empty"
                  className="text-sm text-[var(--color-text-soft)] text-center py-6"
                >
                  No widgets available. Install or enable modules that
                  contribute widgets.
                </div>
              ) : (
                visibleCatalogue.map((w) => {
                  const isPlaced = placedIds.has(w.id);
                  return (
                    <button
                      key={w.id}
                      type="button"
                      data-testid={`dashboard-widget-catalogue-row-${w.id}`}
                      onClick={() => addWidget(w)}
                      disabled={isPlaced}
                      aria-disabled={isPlaced || undefined}
                      aria-label={
                        isPlaced
                          ? `${w.title} — already added to dashboard`
                          : `Add ${w.title} to dashboard`
                      }
                      className={[
                        "text-left px-3 py-2 rounded-[var(--radius-sm)] border-[1.4px]",
                        // Phase J: focus-visible + bumped border alpha
                        // (WCAG 2.4.7 + 1.4.11).
                        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--color-brand-soft)]",
                        isPlaced
                          ? "border-[oklch(0%_0_0_/_0.30)] bg-[oklch(98%_0.003_230)] cursor-not-allowed opacity-60"
                          : "border-[oklch(0%_0_0_/_0.30)] hover:bg-[oklch(99%_0.003_230_/_0.7)]",
                      ].join(" ")}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{w.title}</span>
                        <Chip tone="default">{w.module_id}</Chip>
                        <Chip tone="default">{w.default_size}</Chip>
                        {isPlaced && <Chip tone="green">Already added</Chip>}
                      </div>
                      <div className="text-xs text-[var(--color-text-soft)] mt-0.5">
                        {w.description || "—"}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </DialogContent>
        </Dialog>
      </header>

      {error !== null && (
        <div
          role="alert"
          data-testid="dashboard-error"
          className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div data-testid="dashboard-loading" className="grid grid-cols-4 gap-4">
          <Skeleton className="h-32 col-span-2 rounded-[var(--radius-md)]" />
          <Skeleton className="h-32 col-span-2 rounded-[var(--radius-md)]" />
        </div>
      ) : placedWithDef.length === 0 ? (
        <Card surface="glass-strong" padding="lg">
          <CardContent>
            <div
              data-testid="dashboard-empty"
              className="text-sm text-[var(--color-text-soft)] text-center py-12"
            >
              No widgets yet. Click "Add widget" to start composing your dashboard.
            </div>
          </CardContent>
        </Card>
      ) : (
        <div
          className="grid grid-cols-4 gap-4 auto-rows-[140px]"
          data-testid="dashboard-grid"
        >
          {placedWithDef.map(({ placement, def }) => (
            <Card
              key={placement.widget_id}
              surface="glass-strong"
              data-testid={`dashboard-widget-${placement.widget_id}`}
              className="relative"
              style={{
                gridColumn: `span ${Math.min(placement.w * 2, 4)}`,
                gridRow: `span ${placement.h}`,
              }}
            >
              <button
                type="button"
                aria-label={`Remove ${def?.title ?? placement.widget_id}`}
                data-testid={`dashboard-widget-${placement.widget_id}-remove`}
                onClick={() => removeWidget(placement.widget_id)}
                className="absolute top-2 right-2 grid place-items-center w-7 h-7 rounded-[var(--radius-sm)] text-[var(--color-text-soft)] hover:bg-[oklch(0%_0_0_/_0.05)]"
              >
                <X size={14} />
              </button>
              <CardHeader>
                <CardTitle>{def?.title ?? placement.widget_id}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-[var(--color-text-soft)]">
                  {def?.description ?? "Widget definition not found in this build."}
                </p>
                <div className="mt-2 text-[11px] text-[var(--color-text-faint)] font-mono">
                  {placement.module_id} · {def?.default_size ?? `${placement.w}x${placement.h}`}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
